import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Briefcase, CheckCircle } from 'lucide-react';

const PMPOfficeList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/pmp-office`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load PMP-Office inspections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p>Loading PMP-Office inspections...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Briefcase className="h-6 w-6 text-purple-600" />
          <h2 className="text-2xl font-bold">PMP - Office</h2>
        </div>
        <Button onClick={() => navigate('/field/pmp-office/new')} data-testid="new-pmp-office-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Inspection
        </Button>
      </div>

      {inspections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No PMP-Office inspections found</p>
            <Button onClick={() => navigate('/field/pmp-office/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Inspection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {inspections.map((inspection) => (
            <Card key={inspection.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/field/pmp-office/${inspection.id}`)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{inspection.building_name}</CardTitle>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Address:</strong> {inspection.address}</span>
                      <span>•</span>
                      <span><strong>Date:</strong> {_fmtDate(inspection.date_of_inspection)}</span>
                    </div>
                  </div>
                  {inspection.signature && (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Signed
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <span className="text-gray-500">Inspector:</span>
                      <span className="ml-2 font-medium">{inspection.inspector_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Area Type:</span>
                      <span className="ml-2 font-medium capitalize">{inspection.area_type.replace('_', ' ')}</span>
                    </div>
                    {inspection.floor_number && (
                      <div>
                        <span className="text-gray-500">Floor:</span>
                        <span className="ml-2 font-medium">{inspection.floor_number}</span>
                      </div>
                    )}
                  </div>
                  {inspection.additional_notes && (
                    <p className="text-xs text-gray-500 mt-2">
                      {inspection.additional_notes.substring(0, 100)}{inspection.additional_notes.length > 100 ? '...' : ''}
                    </p>
                  )}
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                    {inspection.photos && inspection.photos.length > 0 && (
                      <span><strong>Photos:</strong> {inspection.photos.length}</span>
                    )}
                    {inspection.forward_to_operations && (
                      <Badge variant="outline" className="text-orange-600">Forwarded to Operations</Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PMPOfficeList;
